/**
 * Funnel SEO cluster: people who already have a CV file (Canva, Europass, AI, ATS check, gaps).
 * Spanish and English. No imports; merged from posts.js.
 */

export default [
  {
    slug: 'cv-canva-y-ats',
    date: '2026-08-19',
    category: 'format',
    related: ['plantillas-cv-compatibles-ats', 'iconos-en-el-cv', 'cv-a-dos-columnas'],
    es: {
      title: 'CV de Canva y ATS: cómo hacer que tu PDF pase el filtro',
      description:
        '¿Tu CV de Canva no pasa el ATS? Prueba de 2 minutos + cómo regenerarlo con ClarCV. Sube tu PDF gratis, sin tarjeta.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Si tu CV de Canva no pasa el ATS, el problema casi nunca es que “no tengas experiencia”. Es el archivo. Canva es una herramienta de diseño: coloca texto en capas, columnas y cuadros. El software de selección (Workday, SuccessFactors, Greenhouse, portales de empleo) intenta leer de arriba abajo y extraer nombre, fechas y puestos. Cuando el orden de capas no coincide con lo que ves en pantalla, el parser mezcla skills con empresas o pierde el email.',
        },
        {
          type: 'p',
          text: 'Esta guía explica qué falla en las plantillas Canva más usadas, cómo hacer la prueba del bloc de notas en dos minutos, cuándo un diseño creativo sí tiene sentido, y cómo pasar de un PDF bonito a uno que el filtro entiende — sin reescribir tu trayectoria a mano. El flujo de ClarCV (clarcv.com) encaja aquí: subes el PDF que ya tienes, la IA señala fricción de estructura, y regeneras una plantilla ATS con texto real.',
        },
        {
          type: 'p',
          text: 'No vas a “hackear” Canva. Vas a decidir si ese archivo es el que envías a un portal o el que enseñas en una entrevista de diseño. Son dos documentos distintos. Quien usa el mismo PDF creativo en InfoJobs, LinkedIn Easy Apply y el email al hiring manager suele perder el primer filtro sin saberlo.',
        },
        { type: 'h2', text: 'Por qué una plantilla Canva rompe el ATS' },
        {
          type: 'p',
          text: 'Canva no exporta como Word. Exporta un lienzo. El texto puede parecer seleccionable y aun así salir desordenado al pegarlo. Los ATS no “ven” el diseño: reconstruyen un flujo de lectura. Sidebars, iconos en lugar de la palabra Email, barras de skill y tablas para simular columnas son los fallos más caros.',
        },
        {
          type: 'ul',
          items: [
            'Text boxes flotantes: el parser lee el lateral antes que tu nombre o al revés.',
            'Dos columnas agresivas: fechas en un lado, título en el otro; el sistema fusiona puestos.',
            'Iconos sin texto plano: el teléfono es un dibujo; el ATS no indexa el número.',
            'PDF con capas o fuentes convertidas a contorno: no puedes copiar tres párrafos seguidos.',
            'Encabezados creativos (“Mi viaje”, “Superpoderes”) que el diccionario del ATS no mapea a Experiencia.',
          ],
        },
        {
          type: 'p',
          text: 'Antes: un CV de marketing en Canva, sidebar oscura, foto grande, Excel dibujado al 80% y el email solo en el pie gráfico. El reclutador humano lo entendería en diez segundos. El ATS a veces registra el nombre y poco más. Después: la misma historia en una columna, email y teléfono en texto al inicio, skills escritas (“Google Ads, GA4, HubSpot”), fechas junto a cada puesto. Ese segundo archivo es el que debe ir al portal.',
        },
        { type: 'h2', text: 'La prueba de dos minutos (hazla hoy)' },
        {
          type: 'p',
          text: 'Abre el PDF de Canva. Selecciona el nombre, el email y tres viñetas de experiencia. Pégalos en el bloc de notas o en un Google Doc vacío. Si el orden sale caótico, si faltan frases o si aparecen símbolos raros, no envíes ese archivo a un ATS. Si no puedes seleccionar texto, el filtro tampoco podrá: es imagen o contorno.',
        },
        {
          type: 'ul',
          items: [
            '¿El email aparece como texto, no como icono?',
            '¿Las fechas van pegadas al puesto, no en una columna huérfana?',
            '¿Las skills son palabras, no barras ni logotipos?',
            '¿Al pegar, Experiencia sigue a Resumen y no al revés?',
            '¿El archivo pesa razonable (fotos enormes bloquean algunos portales)?',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '¿Tu Canva no pasa la prueba del bloc de notas?',
          lead: 'Sube el PDF que ya tienes. ClarCV señala la fricción ATS y te deja regenerar una plantilla de una columna con texto real.',
          from: 'PDF de Canva',
          to: 'CV que el filtro lee',
          button: 'Subir mi CV de Canva',
        },
        { type: 'h2', text: 'Cuándo sí usar Canva' },
        {
          type: 'p',
          text: 'Canva encaja cuando un humano abre el PDF a propósito: networking, portfolio creativo, candidatura enviada por email a un estudio, feria de empleo. En diseño, ilustración o dirección de arte, un CV visual puede ser una muestra. Aun así, conviene un segundo archivo limpio para el portal. El proceso grande filtra primero con software; el portfolio entra después.',
        },
        {
          type: 'p',
          text: 'Si la oferta pide “CV creativo” o adjuntar portfolio, no sustituyas el ATS-friendly por el de Canva: adjunta ambos o enlaza Behance/Dribbble en el CV parseable. Un solo PDF infográfico en un formulario de Workday es la combinación peor.',
        },
        { type: 'h2', text: 'Cómo arreglarlo sin rediseñar a mano' },
        {
          type: 'p',
          text: 'No hace falta reconstruir el CV en Word celda a celda. Extrae el contenido (copia desde el PDF si es seleccionable; si no, desde el editor de Canva) y colócalo en una plantilla de una columna con encabezados estándar: Contacto, Resumen, Experiencia, Formación, Habilidades, Idiomas. Mantén logros y cifras; cambia solo el continente del diseño.',
        },
        {
          type: 'p',
          text: 'En ClarCV subes el PDF o Word que ya tienes. El análisis señala claridad, estructura y señales típicas de fricción ATS (no es un “score mágico” contra Workday; es un diagnóstico accionable). Luego aplicas una plantilla pensada para texto seleccionable e impresión A4. Empieza gratis (3 análisis y 2 descargas cada 30 días, sin tarjeta). Pro son 7 €/mes si iteras a menudo. Si quieres revisar el archivo antes de regenerar, la guía [revisar CV online ATS](/blog/revisar-cv-online-ats) tiene el método en tres capas.',
        },
        {
          type: 'ul',
          items: [
            'Conserva hechos: empresas, fechas, métricas que puedas defender.',
            'Traduce iconos a palabras (teléfono, LinkedIn, carné de conducir).',
            'Renombra secciones creativas a nombres que el ATS espera.',
            'Exporta PDF con texto real; evita “imprimir a imagen”.',
            'Nombre de archivo: Nombre_Apellido_Puesto.pdf, no CV_Canva_final(3).pdf.',
          ],
        },
        { type: 'h2', text: 'Canva no es el enemigo; el canal sí' },
        {
          type: 'p',
          text: 'El error es usar la misma plantilla para todos los canales. Portal + ATS: archivo limpio. Entrevista o email directo a un creativo: puedes enseñar la versión Canva. Si hoy solo tienes la bonita, no la tires: es materia prima. El siguiente paso es un PDF que se pueda copiar y pegar sin que el texto se rompa.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Todas las plantillas de Canva fallan el ATS?',
              a: 'No todas, pero muchas de dos columnas o con text boxes sí. La prueba del bloc de notas manda más que el sello “ATS-friendly” del marketplace.',
            },
            {
              q: '¿Puedo dejar la foto si regenero el CV?',
              a: 'Sí, si el mercado lo espera y no desplaza el contacto. El fallo no es la foto; es el texto irrecuperable o el email solo en el encabezado gráfico.',
            },
            {
              q: '¿Mejor Word o PDF después de salir de Canva?',
              a: 'PDF con texto seleccionable para la mayoría de portales en España. Word si el anuncio lo pide. Nunca un escaneo ni un PNG.',
            },
            {
              q: '¿ClarCV abre archivos de Canva?',
              a: 'Si el PDF tiene texto extraíble, sí. Si es solo imagen, el análisis puede fallar o requerir un PDF con texto; entonces copia el contenido desde Canva y sube un export limpio.',
            },
            {
              q: '¿Debo rediseñar en Canva “más simple”?',
              a: 'Puedes, pero una plantilla hecha para parseo suele ser más fiable que un lienzo simplificado. El objetivo es el portal, no el mockup.',
            },
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Convierte tu Canva en un CV que el portal lee',
          lead: 'Sube el PDF, revisa la vista previa ATS y descarga una plantilla limpia al pagar. Sin reescribir a mano.',
          button: 'Regenerar mi CV',
        },
      ],
    },
    en: {
      title: 'Canva CV and ATS: how to make your PDF pass the filter',
      description:
        'Canva CV failing ATS? 2-minute test + how to regenerate it with ClarCV. Upload your PDF free, no card required.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'If your Canva CV fails ATS, the issue is rarely that you “lack experience”. It is the file. Canva is a design tool: it places text in layers, columns, and boxes. Applicant tracking software tries to read top to bottom and extract name, dates, and roles. When layer order does not match what you see on screen, the parser mixes skills with employers or drops your email.',
        },
        {
          type: 'p',
          text: 'This guide covers what breaks in popular Canva templates, a two-minute notepad test, when a creative layout still makes sense, and how to move from a pretty PDF to one the filter can read — without rewriting your career by hand. ClarCV (clarcv.com) fits here: upload the PDF you already have, get structure feedback, and regenerate an ATS template with real text.',
        },
        {
          type: 'p',
          text: 'You will not “hack” Canva. You will decide whether that file is what you send to a job portal or what you show in a design interview. Those are two documents. Using the same creative PDF for job boards, Easy Apply, and a hiring-manager email often loses the first filter silently.',
        },
        { type: 'h2', text: 'Why a Canva template breaks ATS' },
        {
          type: 'p',
          text: 'Canva does not export like Word. It exports a canvas. Text can look selectable and still paste out of order. ATS tools do not “see” design: they rebuild a reading order. Sidebars, icons instead of the word Email, skill bars, and tables pretending to be columns are the expensive failures.',
        },
        {
          type: 'ul',
          items: [
            'Floating text boxes: the parser reads the sidebar before your name, or the reverse.',
            'Aggressive two-column layouts: dates on one side, titles on the other; roles get merged.',
            'Icons without plain text: the phone is a drawing; the ATS never indexes the number.',
            'Layered PDFs or outlined fonts: you cannot copy three consecutive paragraphs.',
            'Creative headings (“My journey”, “Superpowers”) that the ATS dictionary does not map to Experience.',
          ],
        },
        {
          type: 'p',
          text: 'Before: a marketing CV in Canva, dark sidebar, large photo, Excel drawn at 80%, email only in a graphic footer. A human recruiter would get it in ten seconds. The ATS sometimes stores the name and little else. After: the same story in one column, email and phone as text at the top, skills written as words (“Google Ads, GA4, HubSpot”), dates next to each role. That second file is what the portal should get.',
        },
        { type: 'h2', text: 'The two-minute test (do it today)' },
        {
          type: 'p',
          text: 'Open the Canva PDF. Select your name, email, and three experience bullets. Paste into Notepad or an empty Google Doc. If the order is chaotic, sentences are missing, or odd symbols appear, do not send that file to an ATS. If you cannot select text, neither can the filter: it is an image or outlines.',
        },
        {
          type: 'ul',
          items: [
            'Does the email appear as text, not only as an icon?',
            'Are dates attached to the role, not in an orphan column?',
            'Are skills words, not bars or logos?',
            'When you paste, does Experience still follow Summary?',
            'Is the file a reasonable size (huge photos block some portals)?',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Did your Canva fail the Notepad test?',
          lead: 'Upload the PDF you already have. ClarCV flags ATS friction and lets you regenerate a one-column template with real text.',
          from: 'Canva PDF',
          to: 'A CV the filter can read',
          button: 'Upload my Canva CV',
        },
        { type: 'h2', text: 'When Canva is still the right tool' },
        {
          type: 'p',
          text: 'Canva fits when a human opens the PDF on purpose: networking, creative portfolio, email to a studio, a job fair. In design, illustration, or art direction, a visual CV can be a sample. You still want a second, clean file for the portal. Large processes filter with software first; the portfolio comes later.',
        },
        {
          type: 'p',
          text: 'If the ad asks for a “creative CV” or a portfolio, do not replace the ATS-friendly file with the Canva one: attach both or link Behance/Dribbble on the parseable CV. A single infographic PDF in a Workday form is the worst combination.',
        },
        { type: 'h2', text: 'How to fix it without redesigning by hand' },
        {
          type: 'p',
          text: 'You do not need to rebuild the CV in Word cell by cell. Extract the content (copy from the PDF if it is selectable; otherwise from the Canva editor) and place it in a one-column template with standard headings: Contact, Summary, Experience, Education, Skills, Languages. Keep achievements and figures; change only the design shell.',
        },
        {
          type: 'p',
          text: 'In ClarCV you upload the PDF or Word you already have. Analysis flags clarity, structure, and typical ATS friction (it is not a magic Workday score; it is an actionable diagnosis). Then you apply a template built for selectable text and A4 print. Start free (3 analyses and 2 downloads per 30 days, no card). Pro is €7/month if you iterate often. To review the file before regenerating, see [review your CV for ATS](/blog/revisar-cv-online-ats).',
        },
        {
          type: 'ul',
          items: [
            'Keep facts: employers, dates, metrics you can defend.',
            'Turn icons into words (phone, LinkedIn, driving licence).',
            'Rename creative sections to names the ATS expects.',
            'Export a PDF with real text; avoid “print to image”.',
            'Filename: Name_Surname_Role.pdf, not CV_Canva_final(3).pdf.',
          ],
        },
        { type: 'h2', text: 'Canva is not the enemy; the channel is' },
        {
          type: 'p',
          text: 'The mistake is one template for every channel. Portal + ATS: clean file. Interview or direct email to a creative lead: you can show the Canva version. If today you only have the pretty one, do not throw it away: it is source material. The next step is a PDF you can copy and paste without the text breaking.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do all Canva templates fail ATS?',
              a: 'Not all, but many two-column or text-box layouts do. The notepad test beats a marketplace “ATS-friendly” badge.',
            },
            {
              q: 'Can I keep the photo after regenerating?',
              a: 'Yes, if the market expects it and it does not push contact details aside. The failure is unrecoverable text or an email only in a graphic header — not the photo itself.',
            },
            {
              q: 'Word or PDF after leaving Canva?',
              a: 'Selectable-text PDF for most European portals. Word if the ad asks. Never a scan or a PNG.',
            },
            {
              q: 'Does ClarCV open Canva files?',
              a: 'If the PDF has extractable text, yes. If it is image-only, analysis may fail; copy content from Canva and upload a clean export.',
            },
            {
              q: 'Should I just simplify the Canva design?',
              a: 'You can, but a template built for parsing is usually more reliable than a simplified canvas. The goal is the portal, not the mockup.',
            },
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Turn your Canva file into a portal-ready CV',
          lead: 'Upload the PDF, read the ATS preview, and download a clean template when you pay. No manual rewrite.',
          button: 'Regenerate my CV',
        },
      ],
    },
  },
  {
    slug: 'europass-o-cv-moderno',
    date: '2026-08-19',
    category: 'format',
    related: ['plantillas-cv-compatibles-ats', 'cv-para-sepe', 'mejorar-el-cv-que-ya-tienes'],
    es: {
      title: 'Europass o CV moderno: cuándo dejar el formato oficial',
      description:
        'Cuándo el currículum Europass ayuda (UE, becas, anuncio que lo pide) y cuándo un CV moderno ATS funciona mejor en empresas privadas en España. Cómo cambiar sin perder datos.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Europass o CV moderno no es una guerra de diseño. Es una decisión de canal. El formato Europass (Unión Europea) existe para movilidad, instituciones y procesos que piden un estándar común. En mucha empresa privada española, el mismo archivo se lee como largo, rígido y poco adaptado a la oferta. El reclutador no “odia Europass”; odia un PDF de cuatro páginas que no prioriza el puesto.',
        },
        {
          type: 'p',
          text: 'Si buscas “cambiar cv europass” o “europass ats”, probablemente ya tienes el XML o el PDF oficial y quieres saber si te está costando entrevistas. Esta guía marca cuándo conservarlo, cuándo convertirlo, y cómo pasar el contenido a una plantilla de una o dos páginas parseable. ClarCV sirve para el segundo caso: subes lo que tienes y regeneras un CV moderno sin empezar de cero.',
        },
        {
          type: 'p',
          text: 'No intentes rankear contra la web oficial de la UE ofreciendo “descargar plantilla Europass”. Si el anuncio exige Europass, cúmplelo. Si no lo exige, un CV claro y alineado a la oferta suele ganar en el sector privado.',
        },
        { type: 'h2', text: 'Qué es Europass (sin mito)' },
        {
          type: 'p',
          text: 'Europass es un marco de documentos de la Comisión Europea: currículum, pasaporte de lenguas, suplementos. El CV sigue un esquema fijo (datos personales, experiencia, educación, competencias). Facilita comparar candidatos entre países. No está diseñado para marketing de un perfil ni para filtros ATS de una pyme que usa InfoJobs o Greenhouse.',
        },
        {
          type: 'ul',
          items: [
            'Útil: instituciones UE, agencias europeas, algunas becas, Erasmus+, oferta que pide Europass explícito.',
            'Útil: movilidad entre países cuando el receptor espera ese estándar.',
            'Débil: startups, consultoras y empresas que quieren una página de logros y keywords de la vacante.',
            'Débil: procesos con ATS estricto si el PDF sale denso, con tablas o campos que el parser no mapea bien.',
          ],
        },
        { type: 'h2', text: 'Europass y ATS: qué pasa de verdad' },
        {
          type: 'p',
          text: 'Europass no es “anti-ATS” por decreto. Un PDF con texto seleccionable y secciones reconocibles puede parsearse. El problema habitual es otro: demasiada información genérica, competencias en bloques largos, poca adaptación a las 8–15 keywords de la oferta, y longitud que entierra lo relevante. El filtro busca coincidencias; un Europass no reescrito parece el mismo documento para todos los puestos.',
        },
        {
          type: 'p',
          text: 'Antes: Europass de 3–4 páginas, todas las prácticas desde 2014, competencias copiadas del modelo, sin resumen orientado al rol. Después: CV moderno de 1–2 páginas, resumen de 3 líneas, experiencia con logros, skills que aparecen en el anuncio (si son reales). Misma persona. Distinta señal para el ranking del ATS y para el humano que escanea 20 segundos.',
        },
        { type: 'h2', text: 'Cuándo quedarte con Europass' },
        {
          type: 'ul',
          items: [
            'El anuncio dice “CV Europass” o enlaza el editor oficial.',
            'Postulas a instituciones o programas europeos que lo usan como norma.',
            'El país o el organismo del proceso lo espera (algunos entornos públicos o académicos en la UE).',
            'Es tu única versión y no da tiempo a otra: mejor un Europass completo que un PDF vacío.',
          ],
        },
        { type: 'h2', text: 'Cuándo pasarte a un CV moderno' },
        {
          type: 'p',
          text: 'Si aplicas a empresa privada en España, a roles corporativos o a tech, y nadie ha pedido Europass, cambia. Conserva los datos (fechas, empleadores, formación). Reescribe viñetas como logros. Añade un resumen profesional. Corta cursos irrelevantes. Alinea el lenguaje al anuncio. Ese es el trabajo que el formato oficial no hace por ti.',
        },
        {
          type: 'p',
          text: 'Europass ATS en portales masivos suele perder frente a un PDF de una columna con encabezados Experiencia / Formación / Habilidades. No porque “moderno” sea magia: porque está escrito para esa vacante y cabe en una lectura rápida.',
        },
        { type: 'h2', text: 'Cómo migrar el contenido (30–45 minutos)' },
        {
          type: 'ul',
          items: [
            'Exporta o copia bloque a bloque: contacto, cada puesto, cada título, idiomas.',
            'No copies el bloque de “competencias” genéricas; tradúcelo a skills y a viñetas con prueba.',
            'Elige 1 página (junior/mid) o 2 (senior con trayectoria densa).',
            'Pon las keywords de una oferta real en resumen, experiencia y skills — solo si son ciertas.',
            'Haz la prueba de seleccionar texto en el PDF nuevo.',
          ],
        },
        {
          type: 'p',
          text: 'Sube el PDF Europass a ClarCV si el texto se extrae. El análisis te ayuda a ver densidad, claridad y estructura; luego aplicas una plantilla ATS. Empieza gratis, sin tarjeta. Pro (7 €/mes) si regeneras para varias ofertas. No sustituye el editor oficial cuando el proceso exige Europass.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Los reclutadores descartan Europass a la vista?',
              a: 'No siempre. En privado suele restar si es genérico y largo. En procesos que lo piden, descartar el formato oficial es peor.',
            },
            {
              q: '¿Europass pasa los filtros ATS?',
              a: 'Puede parsearse si hay texto real. Pierde cuando no está adaptado a la oferta o el layout denso confunde el orden de campos.',
            },
            {
              q: '¿Puedo enviar Europass y un CV moderno?',
              a: 'Solo si el anuncio lo permite. Si piden un archivo, envía el que encaja con el canal. Dos PDFs contradictorios generan dudas.',
            },
            {
              q: '¿Debo borrar el perfil Europass?',
              a: 'No. Manténlo para becas e instituciones. Ten una versión moderna para el mercado privado.',
            },
            {
              q: '¿ClarCV genera Europass oficial?',
              a: 'No. Genera CV HTML/PDF profesional ATS. Para el formato UE usa el editor oficial; para empresa privada, una plantilla ClarCV suele ser el destino.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Europass or a modern CV: when to leave the official format',
      description:
        'When a Europass CV helps (EU, grants, ads that require it) and when a modern ATS CV works better in private companies. How to switch without losing data.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Europass versus a modern CV is not a design war. It is a channel decision. The EU Europass format exists for mobility, institutions, and processes that want a shared standard. In much of the Spanish private sector, the same file reads as long, rigid, and poorly tailored. Recruiters do not “hate Europass”; they hate a four-page PDF that does not prioritise the role.',
        },
        {
          type: 'p',
          text: 'If you are searching how to change a Europass CV or whether Europass works with ATS, you likely already have the official XML or PDF. This guide marks when to keep it, when to convert, and how to move the content into a one- or two-page parseable template. ClarCV fits the second case: upload what you have and regenerate a modern CV without starting from zero.',
        },
        {
          type: 'p',
          text: 'Do not try to outrank the official EU site by offering a “download Europass template”. If the ad requires Europass, comply. If it does not, a clear CV aligned to the vacancy usually wins in the private sector.',
        },
        { type: 'h2', text: 'What Europass is (without the myth)' },
        {
          type: 'p',
          text: 'Europass is a European Commission document framework: CV, language passport, supplements. The CV follows a fixed schema (personal details, experience, education, competences). It helps compare candidates across countries. It is not built for marketing a profile or for ATS filters at an SME using a job board or Greenhouse.',
        },
        {
          type: 'ul',
          items: [
            'Useful: EU institutions, European agencies, some grants, Erasmus+, ads that explicitly ask for Europass.',
            'Useful: cross-border mobility when the receiver expects that standard.',
            'Weak: startups, consultancies, and firms that want a page of achievements and vacancy keywords.',
            'Weak: strict ATS processes if the PDF is dense, table-heavy, or maps fields poorly.',
          ],
        },
        { type: 'h2', text: 'Europass and ATS: what actually happens' },
        {
          type: 'p',
          text: 'Europass is not “anti-ATS” by decree. A PDF with selectable text and recognisable sections can parse. The usual problem is different: too much generic information, long competence blocks, little alignment with the ad’s 8–15 keywords, and length that buries what matters. Filters look for matches; an unrewritten Europass looks like the same document for every job.',
        },
        {
          type: 'p',
          text: 'Before: a 3–4 page Europass, every internship since 2014, model competences, no role-oriented summary. After: a modern 1–2 page CV, a 3-line summary, achievement bullets, skills that appear in the ad (when true). Same person. Different signal for ATS ranking and for a human scanning for 20 seconds.',
        },
        { type: 'h2', text: 'When to keep Europass' },
        {
          type: 'ul',
          items: [
            'The ad says “Europass CV” or links the official editor.',
            'You are applying to EU institutions or programmes that use it as the norm.',
            'The country or body expects it (some public or academic EU settings).',
            'It is your only version and there is no time: a complete Europass beats an empty PDF.',
          ],
        },
        { type: 'h2', text: 'When to switch to a modern CV' },
        {
          type: 'p',
          text: 'If you apply to private companies in Spain, corporate roles, or tech, and nobody asked for Europass, switch. Keep the facts (dates, employers, education). Rewrite bullets as achievements. Add a professional summary. Cut irrelevant courses. Align language to the ad. That is the work the official format does not do for you.',
        },
        {
          type: 'p',
          text: 'A Europass in high-volume portals often loses to a one-column PDF with Experience / Education / Skills headings. Not because “modern” is magic: because it is written for that vacancy and fits a fast read.',
        },
        { type: 'h2', text: 'How to migrate the content (30–45 minutes)' },
        {
          type: 'ul',
          items: [
            'Export or copy block by block: contact, each role, each degree, languages.',
            'Do not paste generic “competences”; turn them into skills and evidenced bullets.',
            'Choose 1 page (junior/mid) or 2 (dense senior track).',
            'Place keywords from a real ad in summary, experience, and skills — only if they are true.',
            'Test selecting text in the new PDF.',
          ],
        },
        {
          type: 'p',
          text: 'Upload the Europass PDF to ClarCV if text extracts. Analysis helps you see density, clarity, and structure; then apply an ATS template. Start free, no card. Pro (€7/month) if you regenerate for several ads. It does not replace the official editor when the process requires Europass.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Do recruiters reject Europass on sight?',
              a: 'Not always. In the private sector it often hurts if it is generic and long. In processes that require it, skipping the official format is worse.',
            },
            {
              q: 'Does Europass pass ATS filters?',
              a: 'It can parse if there is real text. It loses when it is not tailored to the ad or a dense layout confuses field order.',
            },
            {
              q: 'Can I send Europass and a modern CV?',
              a: 'Only if the ad allows it. If they want one file, send the one that fits the channel. Two contradictory PDFs create doubt.',
            },
            {
              q: 'Should I delete my Europass profile?',
              a: 'No. Keep it for grants and institutions. Keep a modern version for the private market.',
            },
            {
              q: 'Does ClarCV generate official Europass?',
              a: 'No. It generates professional ATS HTML/PDF CVs. For the EU format use the official editor; for private firms, a ClarCV template is usually the destination.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'revisar-cv-online-ats',
    date: '2026-08-19',
    category: 'ats',
    related: ['cv-para-portales-empleo', 'cv-para-filtros-ats', 'mejorar-el-cv-que-ya-tienes'],
    es: {
      title: 'Cómo revisar si tu CV pasa el ATS (y qué hacer después)',
      description:
        'Cómo analizar y revisar un currículum online antes de enviarlo: prueba de texto, estructura, keywords de la oferta y cuándo usar un verificador o ClarCV. Sin score mágico.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Revisar un CV online no es obtener un número de 0 a 100 y relajarte. Un “verificador ATS” o un analizador de currículum puede señalar formato, keywords y clichés. Ninguno es el software real de la empresa (Workday, SuccessFactors, el portal de InfoJobs). La pregunta útil es: ¿un parser mediocre extraería mi contacto, mis puestos y las palabras de la oferta — y un humano entendería el impacto en 20 segundos?',
        },
        {
          type: 'p',
          text: 'Si buscas analizar CV, corrector de CV o comprobar si tu CV pasa el ATS, esta guía te da un método en tres capas: archivo (¿se puede seleccionar el texto?), estructura (¿secciones y fechas claras?), contenido (¿keywords verdaderas de una oferta concreta?). Después, qué hacer con el diagnóstico: editar, adaptar a la vacante o regenerar plantilla. ClarCV cubre análisis de claridad y estructura sobre el PDF o Word que subes, más plantillas parseables — no un porcentaje inventado “tipo Jobscan”.',
        },
        {
          type: 'p',
          text: 'Empieza por el archivo que ya tienes. No por una plantilla vacía. El funnel correcto es: revisar → corregir fricción → adaptar a una oferta → descargar. La vista previa ATS de ClarCV existe para ese primer paso, sin tarjeta.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Revisa tu CV antes de enviarlo',
          lead: 'Sube el PDF o Word que ya tienes. ClarCV señala si el texto se extrae bien y qué puede romper un filtro ATS.',
          from: 'Tu archivo actual',
          to: 'Diagnóstico en minutos',
          button: 'Subir mi CV',
        },
        { type: 'h2', text: 'Capa 1: el archivo (dos minutos)' },
        {
          type: 'p',
          text: 'Abre el PDF. Copia nombre, email y un bloque de experiencia al bloc de notas. Si el orden explota, si faltan frases o si no hay texto seleccionable, el ATS fallará aunque el diseño convenza a tu primo. Corrige esto antes de obsesionarte con keywords.',
        },
        {
          type: 'ul',
          items: [
            'Texto seleccionable en todo el cuerpo, no solo en el título.',
            'Email y teléfono en texto al inicio, no solo en encabezado gráfico.',
            'PDF o DOCX según pida el anuncio; nunca foto ni PNG.',
            'Peso razonable; fotos a 300 dpi innecesarias bloquean subidas.',
            'Nombre de archivo profesional: Ana_Garcia_Data_Analyst.pdf.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: '¿Tu PDF pasa la prueba del bloc de notas?',
          lead: 'Si al pegar el texto el orden explota, el ATS fallará igual. Compruébalo con tu archivo, gratis y sin tarjeta.',
          button: 'Analizar mi CV',
        },
        { type: 'h2', text: 'Capa 2: estructura que un parser espera' },
        {
          type: 'p',
          text: 'Encabezados estándar (Experiencia, Formación, Habilidades). Fechas junto a cada puesto. Una columna principal. Skills en palabras, no en barras. Si usaste Canva o Europass denso, esta capa suele ser el cuello de botella. Detalle en las guías de Canva y Europass de este blog.',
        },
        { type: 'h2', text: 'Capa 3: keywords de UNA oferta (no de internet)' },
        {
          type: 'p',
          text: 'Pega una oferta real. Subraya 8–15 términos: herramientas, metodologías, título de puesto. Márcalos en tu CV. Los huecos importantes solo se cubren si son verdad. Un corrector genérico que te pide “liderazgo” diez veces no es un análisis ATS; es relleno. La coincidencia útil es oferta ↔ logros, no una lista negra de adjetivos.',
        },
        {
          type: 'p',
          text: 'Antes: “Responsable de mejorar procesos y orientar a resultados.” Después: “Automatizé el reporting semanal en Looker para 12 cuentas; el equipo dejó de montar el Excel a mano (unas 4 horas/semana).” El analizador y el humano encuentran Looker; tú puedes contarlo en entrevista.',
        },
        { type: 'h2', text: 'Qué hacen (y no hacen) los checkers online' },
        {
          type: 'p',
          text: 'Herramientas tipo ATS resume checker comparan tu texto con reglas o con una oferta que pegas. Sirven para ver keywords que se te escaparon y errores de formato groseros. No demuestran que “pasarás” el filtro de esa empresa. Tratan tu archivo como ellos parsean, no como parsea el cliente. Úsalos como lista de deberes, no como certificado.',
        },
        {
          type: 'ul',
          items: [
            'Útil: te recuerdan secciones vacías, fechas raras, PDF-imagen.',
            'Útil: listan términos de la oferta que no aparecen.',
            'Poco útil: un score único sin contexto de sector ni de parser real.',
            'Peligroso: rellenar el CV con keywords que no puedes defender.',
          ],
        },
        { type: 'h2', text: 'Qué hacer después del diagnóstico' },
        {
          type: 'p',
          text: 'Si falla la capa 1–2, cambia plantilla antes de reescribir logros. Si el archivo es limpio pero genérico, adapta a la oferta (20 minutos, sin mentir). Si el texto está bien y el diseño no, regenera PDF ATS. En ClarCV: sube el archivo, lee el análisis, aplica plantilla, descarga. Gratis para probar; Pro si envías muchas versiones.',
        },
        {
          type: 'p',
          text: 'No persigas un 98% en un checker. Persigue: texto extraíble, historia defendible, keywords de esa vacante, una página o dos según seniority. Eso es revisar un currículum de verdad.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Existe un verificador ATS oficial de las empresas?',
              a: 'No para candidatos. Cada empresa configura el suyo. Los checkers públicos son aproximaciones.',
            },
            {
              q: '¿Un 80% de match garantiza entrevistas?',
              a: 'No. El ranking interno usa más reglas (ubicación, años, exclusión). El match es una señal, no una oferta.',
            },
            {
              q: '¿Puedo revisar el CV gratis en ClarCV?',
              a: 'Sí: cuenta, 1 análisis ATS de vista previa cada 30 días, sin tarjeta. El PDF limpio se descarga al pagar. El análisis cubre claridad y estructura sobre tu archivo, no un score contra un ATS concreto.',
            },
            {
              q: '¿Debo pegar la oferta en ClarCV?',
              a: 'El flujo principal analiza el documento. Para alinear keywords, usa también la oferta a mano o la guía de adaptar el CV a una vacante.',
            },
            {
              q: '¿El corrector ortográfico basta?',
              a: 'No. La ortografía importa al humano; el ATS se cae por parseo y por keywords, no por una tilde.',
            },
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Listo para revisar tu currículum de verdad',
          lead: 'Sube el PDF, lee la vista previa y, si encaja, descarga una plantilla ATS al pagar.',
          button: 'Subir mi CV',
        },
      ],
    },
    en: {
      title: 'How to check if your CV passes ATS (and what to do next)',
      description:
        'How to review a résumé online before you apply: text test, structure, job-ad keywords, and when to use a checker or ClarCV. No magic score.',
      readMinutes: 14,
      sections: [
        {
          type: 'p',
          text: 'Reviewing a CV online is not getting a 0–100 number and relaxing. An “ATS checker” or résumé analyser can flag format, keywords, and clichés. None of them is the company’s real software (Workday, SuccessFactors, a job-board parser). The useful question is: would a mediocre parser extract my contact details, roles, and the ad’s words — and would a human see impact in 20 seconds?',
        },
        {
          type: 'p',
          text: 'If you want to analyse a CV, use a résumé checker, or test whether your CV passes ATS, this guide uses three layers: file (can you select the text?), structure (clear sections and dates?), content (true keywords from one specific ad?). Then what to do with the diagnosis: edit, tailor, or regenerate a template. ClarCV analyses clarity and structure on the PDF or Word you upload, plus parseable templates — not an invented Jobscan-style percentage.',
        },
        {
          type: 'p',
          text: 'Start from the file you already have. Not from a blank template. The right funnel is: review → fix friction → tailor to one ad → download. ClarCV’s free ATS preview exists for that first step, no card.',
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Review your CV before you apply',
          lead: 'Upload the PDF or Word you already have. ClarCV flags whether text extracts cleanly and what may break an ATS filter.',
          from: 'Your current file',
          to: 'Diagnosis in minutes',
          button: 'Upload my CV',
        },
        { type: 'h2', text: 'Layer 1: the file (two minutes)' },
        {
          type: 'p',
          text: 'Open the PDF. Copy name, email, and one experience block into Notepad. If the order explodes, sentences vanish, or text is not selectable, ATS will fail even if your cousin likes the design. Fix this before obsessing over keywords.',
        },
        {
          type: 'ul',
          items: [
            'Selectable text in the whole body, not only the title.',
            'Email and phone as text at the top, not only in a graphic header.',
            'PDF or DOCX as the ad requests; never a photo or PNG.',
            'Reasonable file size; unnecessary 300 dpi photos block uploads.',
            'Professional filename: Ana_Garcia_Data_Analyst.pdf.',
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Does your PDF pass the Notepad test?',
          lead: 'If pasted text comes out in chaos, ATS will fail too. Check your file free, no card required.',
          button: 'Analyse my CV',
        },
        { type: 'h2', text: 'Layer 2: structure a parser expects' },
        {
          type: 'p',
          text: 'Standard headings (Experience, Education, Skills). Dates next to each role. One main column. Skills as words, not bars. If you used Canva or a dense Europass, this layer is often the bottleneck. See the Canva and Europass guides on this blog.',
        },
        { type: 'h2', text: 'Layer 3: keywords from ONE ad (not from the internet)' },
        {
          type: 'p',
          text: 'Paste a real vacancy. Highlight 8–15 terms: tools, methods, job title. Mark them on your CV. Important gaps are filled only if they are true. A generic checker asking for “leadership” ten times is not ATS analysis; it is stuffing. Useful overlap is ad ↔ achievements, not a blacklist of adjectives.',
        },
        {
          type: 'p',
          text: 'Before: “Responsible for improving processes and being results-oriented.” After: “Automated weekly reporting in Looker for 12 accounts; the team stopped building the spreadsheet by hand (about 4 hours/week).” Checker and human find Looker; you can tell the story in interview.',
        },
        { type: 'h2', text: 'What online checkers do (and do not)' },
        {
          type: 'p',
          text: 'ATS résumé checkers compare your text with rules or with a pasted job ad. They help you spot missing keywords and gross format errors. They do not prove you will “pass” that company’s filter. They parse like they parse, not like the client. Use them as a to-do list, not a certificate.',
        },
        {
          type: 'ul',
          items: [
            'Useful: empty sections, odd dates, image-only PDFs.',
            'Useful: ad terms that never appear.',
            'Weak: a single score with no sector or real parser context.',
            'Dangerous: stuffing keywords you cannot defend.',
          ],
        },
        { type: 'h2', text: 'What to do after the diagnosis' },
        {
          type: 'p',
          text: 'If layers 1–2 fail, change template before rewriting achievements. If the file is clean but generic, tailor to the ad (20 minutes, no lying). If the text is fine and the layout is not, regenerate an ATS PDF. In ClarCV: upload, read the analysis, apply a template, download. Free to try; Pro if you ship many versions.',
        },
        {
          type: 'p',
          text: 'Do not chase 98% on a checker. Chase: extractable text, a defensible story, keywords from that vacancy, one or two pages by seniority. That is a real CV review.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Is there an official company ATS checker for applicants?',
              a: 'No. Each company configures its own. Public checkers are approximations.',
            },
            {
              q: 'Does an 80% match guarantee interviews?',
              a: 'No. Internal ranking uses more rules (location, years, knock-outs). Match is a signal, not an offer.',
            },
            {
              q: 'Can I review my CV free on ClarCV?',
              a: 'Yes: account, one ATS preview every 30 days, no card. The clean PDF downloads when you pay. Analysis covers clarity and structure on your file, not a score against a specific ATS.',
            },
            {
              q: 'Should I paste the job ad into ClarCV?',
              a: 'The main flow analyses the document. To align keywords, also use the ad by hand or the tailor-your-CV guide.',
            },
            {
              q: 'Is a spellchecker enough?',
              a: 'No. Spelling matters to humans; ATS fails on parsing and keywords, not on one missing accent.',
            },
          ],
        },
        {
          type: 'cta',
          variant: 'mid',
          title: 'Ready for a real CV review',
          lead: 'Upload your PDF, read the analysis, and download an ATS template if needed. 3 free analyses every 30 days.',
          button: 'Upload my CV',
        },
      ],
    },
  },
  {
    slug: 'hueco-laboral-en-el-cv',
    date: '2026-08-19',
    category: 'career',
    related: ['cambiar-de-sector-cv', 'cv-mayores-de-50', 'mejorar-el-cv-que-ya-tienes'],
    es: {
      title: 'Hueco laboral en el CV: cómo explicarlo (y un despido) sin hundir la candidatura',
      description:
        'Cómo tratar un gap en el currículum, un despido o meses de búsqueda: qué poner, qué no inventar, y cómo reescribir la experiencia que ya tienes para que el ATS y el humano no se queden solo con el vacío.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Un hueco laboral en el CV no es un delito. Tratarlo mal sí quema entrevistas. El ATS no “suspende” un gap por magia; indexa fechas. El humano, en 20 segundos, ve un blanco y rellena con la historia peor (inactividad, conflicto, mentira). Tu trabajo es dar una etiqueta honesta y corta, y desplazar la atención a lo que sí entregaste antes y después.',
        },
        {
          type: 'p',
          text: 'Esta guía cubre gaps de meses o años, despido (objetivo, ERE, fin de contrato, no superación de prueba) y cómo no inflar títulos para tapar el agujero. Si ya tienes un PDF, no lo tires: reescribe fechas, una línea de contexto y los logros de los roles adyacentes. ClarCV sirve para regenerar la plantilla y la claridad; la honestidad sigue siendo tuya.',
        },
        {
          type: 'p',
          text: 'No uses “consultor freelance” si no facturaste. No pongas un cargo en una empresa amiga. Los gaps se descubren en SS, LinkedIn y referencias. El filtro automático no es el problema principal; la entrevista sí lo será si el PDF contradice la realidad.',
        },
        { type: 'h2', text: 'Qué cuenta como hueco (y qué no)' },
        {
          type: 'p',
          text: 'Un mes entre empleos casi nunca merece una sección. Tres a seis meses en el mismo año, o un año sabático, sí se notan si dejas las fechas en años (2019–2021) sin mes. Usa MM/AAAA. Encadena contratos temporales con claridad en lugar de un bloque vacío mal explicado.',
        },
        {
          type: 'ul',
          items: [
            'Cuidado, formación a tiempo completo, mudanza, visado: una línea basta (“Cuidado familiar, 03/2023–11/2023”).',
            'Búsqueda activa tras un ERE: “Búsqueda de empleo tras reestructuración, 2024” más 2–3 hechos (cursos reales, voluntariado, proyecto).',
            'Salud: no detailles diagnóstico. “Permiso médico, recuperado y disponible” si te sientes cómodo; si no, fechas + “periodo fuera del mercado” y prepara la frase oral.',
            'Freelance real: clientes, entregables, fechas. Freelance ficticio es peor que el hueco.',
          ],
        },
        { type: 'h2', text: 'Cómo explicar un despido en el CV' },
        {
          type: 'p',
          text: 'El CV no es el sitio para un alegato. Fin de contrato, fin de proyecto, ERE o “puesto amortizado” se pueden decir en una media línea en la cabecera del rol o dejarse para la entrevista. No escribas “me echaron injustamente”. No ataques a la empresa. El documento debe mostrar el último logro, no el conflicto.',
        },
        {
          type: 'p',
          text: 'Antes: desaparece 2023 entero y el siguiente puesto empieza en 2025. El lector asume lo peor. Después: “Analista, Empresa X, 01/2021–06/2023 (fin de proyecto / ERE). Implementé el dashboard de ventas en Power BI usado por 8 delegaciones.” El hueco siguiente, si existe, lleva su propia línea. Misma verdad, menos niebla.',
        },
        { type: 'h2', text: 'Dónde ponerlo (resumen vs experiencia)' },
        {
          type: 'p',
          text: 'Si el gap es reciente y largo, una frase en el resumen evita que el ATS y el humano tropiecen: “Tras un ERE en 2024, busco rol de operations en logística; 6 años en planificación de almacén.” Si el gap es antiguo y la trayectoria posterior es sólida, no lo conviertas en el tema del perfil. Detalle mínimo en experiencia; peso en logros recientes.',
        },
        { type: 'h2', text: 'ATS: fechas y palabras, no cuentos' },
        {
          type: 'p',
          text: 'Los parsers leen rangos. Fechas contradictorias con LinkedIn generan desconfianza cuando un humano compara. Mantén el mismo mes/año en ambos. No rellenes el gap con keywords de la oferta. Sí puedes listar un curso o certificación real en Formación durante ese periodo.',
        },
        { type: 'h2', text: 'Qué hacer con el PDF que ya tienes' },
        {
          type: 'p',
          text: 'Añade meses a las fechas. Inserta una línea de contexto donde el blanco grita. Reescribe las viñetas del último empleo con impacto (números, alcance). Corta hobbies que intentan “llenar”. Sube el archivo a ClarCV para estructura ATS y plantilla limpia; revisa tú las frases del gap. Empieza gratis, sin tarjeta.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Debo mentir en las fechas para tapar un mes?',
              a: 'No. Un mes no suele importar. Mentir en meses sí importa cuando contrastan referencias o vida laboral.',
            },
            {
              q: '¿Pongo “en búsqueda activa” como puesto?',
              a: 'Mejor una línea de contexto que un cargo falso. Si estudiaste o cuidaste, nómbralo con fechas.',
            },
            {
              q: '¿El ATS descarta gaps?',
              a: 'Algunos flujos filtran por años continuos; la mayoría no. El daño típico es humano: duda y menos entrevistas. Claridad reduce esa duda.',
            },
            {
              q: '¿Hablo del despido en la carta?',
              a: 'ClarCV no genera carta. Si escribes una, una frase factual basta; el CV no debe ser un alegato.',
            },
            {
              q: '¿LinkedIn igual que el CV?',
              a: 'Sí en fechas y empleadores. El texto del gap puede ser aún más corto en LinkedIn; la coherencia es lo que se audita.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Employment gaps on a CV: how to explain them (and a layoff) without sinking the application',
      description:
        'How to handle a résumé gap, a layoff, or months of job search: what to write, what not to invent, and how to rewrite the file you already have so ATS and humans do not only see the blank.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'An employment gap on a CV is not a crime. Handling it badly does burn interviews. ATS tools do not magically “fail” a gap; they index dates. A human, in 20 seconds, sees a blank and fills in the worst story (inactivity, conflict, lying). Your job is a short honest label, then shift attention to what you delivered before and after.',
        },
        {
          type: 'p',
          text: 'This guide covers gaps of months or years, layoffs (redundancy, contract end, failed probation), and how not to inflate titles to hide the hole. If you already have a PDF, do not throw it away: rewrite dates, one context line, and achievements on the adjacent roles. ClarCV helps regenerate template and clarity; honesty stays yours.',
        },
        {
          type: 'p',
          text: 'Do not use “freelance consultant” if you never invoiced. Do not add a role at a friend’s company. Gaps show up in records, LinkedIn, and references. The automatic filter is not the main risk; the interview is, if the PDF contradicts reality.',
        },
        { type: 'h2', text: 'What counts as a gap (and what does not)' },
        {
          type: 'p',
          text: 'One month between jobs almost never needs a section. Three to six months in the same year, or a sabbatical year, do show if you only list years (2019–2021) without months. Use MM/YYYY. Chain temporary contracts clearly instead of a poorly explained empty block.',
        },
        {
          type: 'ul',
          items: [
            'Caregiving, full-time study, relocation, visa: one line is enough (“Family care, 03/2023–11/2023”).',
            'Active search after redundancy: “Job search after restructuring, 2024” plus 2–3 real facts (courses, volunteering, a project).',
            'Health: do not detail a diagnosis. “Medical leave, recovered and available” if you are comfortable; otherwise dates + “time out of the market” and prepare the spoken line.',
            'Real freelance: clients, deliverables, dates. Fake freelance is worse than the gap.',
          ],
        },
        { type: 'h2', text: 'How to explain a layoff on the CV' },
        {
          type: 'p',
          text: 'The CV is not the place for a plea. Contract end, project end, redundancy, or “role eliminated” can sit in half a line on the role header or wait for the interview. Do not write “I was fired unfairly”. Do not attack the employer. The document should show the last achievement, not the conflict.',
        },
        {
          type: 'p',
          text: 'Before: 2023 vanishes and the next role starts in 2025. The reader assumes the worst. After: “Analyst, Company X, 01/2021–06/2023 (project end / redundancy). Built the Power BI sales dashboard used by 8 regional teams.” Any later gap gets its own line. Same truth, less fog.',
        },
        { type: 'h2', text: 'Where to put it (summary vs experience)' },
        {
          type: 'p',
          text: 'If the gap is recent and long, one sentence in the summary stops ATS and humans stumbling: “After a 2024 redundancy, I am targeting operations roles in logistics; 6 years in warehouse planning.” If the gap is old and later track record is solid, do not make it the theme. Minimal detail in experience; weight on recent achievements.',
        },
        { type: 'h2', text: 'ATS: dates and words, not stories' },
        {
          type: 'p',
          text: 'Parsers read ranges. Dates that contradict LinkedIn create distrust when a human compares. Keep the same month/year in both. Do not fill the gap with vacancy keywords. You can list a real course or certification under Education during that period.',
        },
        { type: 'h2', text: 'What to do with the PDF you already have' },
        {
          type: 'p',
          text: 'Add months to dates. Insert a context line where the blank shouts. Rewrite the last job’s bullets with impact (numbers, scope). Cut hobbies that try to “fill space”. Upload the file to ClarCV for ATS structure and a clean template; you review the gap wording. Start free, no card.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Should I lie about dates to hide one month?',
              a: 'No. One month rarely matters. Lying about months does matter when references or records are checked.',
            },
            {
              q: 'Should I list “actively looking” as a job?',
              a: 'A context line beats a fake title. If you studied or provided care, name it with dates.',
            },
            {
              q: 'Does ATS reject gaps?',
              a: 'Some flows filter on continuous years; most do not. Typical damage is human: doubt and fewer interviews. Clarity reduces that doubt.',
            },
            {
              q: 'Do I discuss the layoff in a cover letter?',
              a: 'ClarCV does not generate letters. If you write one, one factual sentence is enough; the CV should not be a plea.',
            },
            {
              q: 'Should LinkedIn match the CV?',
              a: 'Yes on dates and employers. Gap copy can be even shorter on LinkedIn; consistency is what gets audited.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'mejorar-cv-con-ia',
    date: '2026-08-19',
    category: 'ai',
    related: ['chatgpt-o-clarcv-para-tu-cv', 'revisar-cv-online-ats', 'mejorar-el-cv-que-ya-tienes'],
    es: {
      title: 'Mejorar el CV con IA: qué hace una herramienta (y qué no un chat)',
      description:
        'Cómo optimizar un currículum con inteligencia artificial si ya tienes un PDF: análisis, plantilla ATS y límites. Diferencia entre un chat y una herramienta de CV como ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Mejorar un CV con IA no es pegar el archivo en un chat y esperar un milagro. Una herramienta de currículum con inteligencia artificial trabaja sobre tu documento: extrae texto, señala problemas de claridad y estructura, y puede regenerar un PDF pensado para filtros ATS. Un chat (ChatGPT, Gemini, Claude) redacta párrafos si le das contexto. No parsea bien un Canva, no garantiza texto seleccionable y no es tu historial laboral.',
        },
        {
          type: 'p',
          text: 'Si buscas herramienta IA para CV u optimizar CV con IA, el intent suele ser “tengo un archivo y quiero el siguiente paso”, no “quiero un prompt”. Esta guía compara ese intent con el cluster de ChatGPT de este blog, lista un flujo seguro (hechos → IA → tú revisas → archivo ATS) y sitúa ClarCV: subida de PDF/Word, análisis, plantillas, descarga. Empieza gratis, 7 €/mes si Pro.',
        },
        {
          type: 'p',
          text: 'La IA no sustituye ofertas reales ni métricas que puedas defender. Optimizar es alinear lo verdadero con el lenguaje del puesto y con un formato que el software lee. Inventar un 40% de churn es el fallo más caro, lo haga un modelo o un humano.',
        },
        { type: 'h2', text: 'Chat vs herramienta: dos trabajos distintos' },
        {
          type: 'ul',
          items: [
            'Chat: reescribe viñetas, resume, traduce, extrae keywords si pegas la oferta.',
            'Herramienta de CV: archivo de entrada y de salida, plantilla, márgenes A4, análisis del documento.',
            'Chat: el copy-paste a Canva o a columnas rompe el ATS (ver guía Canva).',
            'Herramienta: el valor es el PDF parseable, no el tono “profesional” del párrafo.',
          ],
        },
        {
          type: 'p',
          text: 'Si solo quieres frases, el chat basta y es más barato. Si quieres enviar mañana a un portal, necesitas el archivo. Mezclar ambos está bien: chat para un borrador de logros, ClarCV para el continente ATS. Hay una guía específica ChatGPT vs herramienta de CV en este blog; aquí el foco es “optimizar el que ya tengo”.',
        },
        { type: 'h2', text: 'Flujo para optimizar el PDF que ya tienes' },
        {
          type: 'p',
          text: 'Primero, prueba de texto (seleccionar y pegar). Segundo, una oferta pegada al lado: 8–15 keywords reales. Tercero, análisis de estructura (secciones, fechas, skills en palabras). Cuarto, reescritura de viñetas débiles con verbo + alcance + resultado. Quinto, PDF de una columna. La IA acelera el tercero y el cuarto; tú firmas el quinto.',
        },
        {
          type: 'p',
          text: 'Antes: CV de 2019, dos columnas, “responsable de diversas funciones”. Después: mismo empleo, “Coordiné 3 rutas de distribución (12 tiendas) y recorté incidencias de entrega de 9 a 4 por semana.” La herramienta no “inventó logística”; tú diste el hecho y la IA ayudó a ordenarlo.',
        },
        { type: 'h2', text: 'Privacidad: no es el mismo producto' },
        {
          type: 'p',
          text: 'Pegar un CV en un chat de consumo no es lo mismo que una API de empresa con política de no entrenamiento y cuenta. No subas DNI ni dirección exacta donde no haga falta. Detalle en la guía de privacidad e IA. En ClarCV el archivo viaja para analizar y generar; no se vende a anunciantes.',
        },
        { type: 'h2', text: 'Qué no promete ninguna IA seria' },
        {
          type: 'ul',
          items: [
            'Un empleo. Solo un documento más claro.',
            'Un score ATS oficial de esa empresa.',
            'Un Europass certificado ni un Canva “aprobado”.',
            'Mentir mejor. Mentir peor, de hecho: más fluido y más comprobable.',
          ],
        },
        { type: 'h2', text: 'Cuándo usar ClarCV en este flujo' },
        {
          type: 'p',
          text: 'Cuando el cuello de botella es el archivo: subes, lees el análisis, aplicas plantilla ATS, descargas. Tres análisis y dos descargas cada 30 días en el plan gratis. Si tu cuello de botella es “no sé qué fui en aquel trabajo”, primero inventario en papel; la IA no recuerda tu vida. Interfaz ES/EN; el CV de salida puede seguir el idioma de la oferta.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Mejorar el CV con IA sustituye a un coach?',
              a: 'No. Sustituye parte del formateo y de la primera pasada de claridad. Un coach o un referente de sector siguen siendo útiles para posicionamiento.',
            },
            {
              q: '¿Puedo usar solo ChatGPT?',
              a: 'Para redactar, sí. Para el PDF que envías a un ATS, el chat se queda corto. Combínalo o usa una herramienta de CV.',
            },
            {
              q: '¿La IA de ClarCV es ChatGPT?',
              a: 'El producto usa modelos vía API para análisis y reescritura, no el chat de consumo. No impersonamos a ChatGPT en la marca.',
            },
            {
              q: '¿Optimizar con IA hace el CV genérico?',
              a: 'Si no das hechos, sí. Si das inventario y una oferta, no. Revisa clichés antes de descargar.',
            },
            {
              q: '¿Hay que pagar para probar?',
              a: 'No. Cuenta gratis, sin tarjeta, con cupo mensual de análisis y descargas. Pro (7 €/mes) si iteras mucho.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Improve a CV with AI: what a tool does (and what a chat does not)',
      description:
        'How to optimise a résumé with AI when you already have a PDF: analysis, ATS template, and limits. Chat versus a CV tool such as ClarCV.',
      readMinutes: 13,
      sections: [
        {
          type: 'p',
          text: 'Improving a CV with AI is not pasting the file into a chat and hoping for a miracle. A CV tool with artificial intelligence works on your document: it extracts text, flags clarity and structure issues, and can regenerate a PDF built for ATS filters. A chat (ChatGPT, Gemini, Claude) drafts paragraphs if you give context. It does not parse Canva well, does not guarantee selectable text, and is not your employment history.',
        },
        {
          type: 'p',
          text: 'If you want an AI CV tool or to optimise a CV with AI, intent is usually “I have a file and want the next step”, not “I want a prompt”. This guide maps that intent to the ChatGPT cluster on this blog, lists a safe flow (facts → AI → you review → ATS file), and places ClarCV: PDF/Word upload, analysis, templates, download. Start free, €7/month if Pro.',
        },
        {
          type: 'p',
          text: 'AI does not replace real job ads or metrics you can defend. Optimising means aligning what is true with the role’s language and with a format software can read. Inventing 40% churn is the most expensive failure, whether a model or a human does it.',
        },
        { type: 'h2', text: 'Chat vs tool: two different jobs' },
        {
          type: 'ul',
          items: [
            'Chat: rewrites bullets, summarises, translates, extracts keywords if you paste the ad.',
            'CV tool: input and output file, template, A4 margins, document analysis.',
            'Chat: copy-paste into Canva or columns breaks ATS (see the Canva guide).',
            'Tool: the value is the parseable PDF, not a “professional” tone in a paragraph.',
          ],
        },
        {
          type: 'p',
          text: 'If you only need sentences, chat is enough and cheaper. If you need to apply to a portal tomorrow, you need the file. Mixing both is fine: chat for a draft of achievements, ClarCV for the ATS shell. There is a dedicated ChatGPT vs CV-tool guide on this blog; here the focus is “optimise the one I already have”.',
        },
        { type: 'h2', text: 'Flow to optimise the PDF you already have' },
        {
          type: 'p',
          text: 'First, the text test (select and paste). Second, one ad beside it: 8–15 real keywords. Third, structure analysis (sections, dates, skills as words). Fourth, rewrite weak bullets with verb + scope + result. Fifth, a one-column PDF. AI speeds up the third and fourth; you sign off the fifth.',
        },
        {
          type: 'p',
          text: 'Before: a 2019 CV, two columns, “responsible for various functions”. After: the same job, “Coordinated 3 distribution routes (12 stores) and cut delivery incidents from 9 to 4 per week.” The tool did not “invent logistics”; you supplied the fact and AI helped order it.',
        },
        { type: 'h2', text: 'Privacy: not the same product' },
        {
          type: 'p',
          text: 'Pasting a CV into a consumer chat is not the same as an enterprise API with a no-training policy and an account. Do not upload ID numbers or exact addresses where they are not needed. Detail in the privacy and AI guide. In ClarCV the file travels to analyse and generate; it is not sold to advertisers.',
        },
        { type: 'h2', text: 'What no serious AI promises' },
        {
          type: 'ul',
          items: [
            'A job. Only a clearer document.',
            'An official ATS score from that company.',
            'A certified Europass or an “approved” Canva file.',
            'Better lying. Worse lying, actually: more fluent and more checkable.',
          ],
        },
        { type: 'h2', text: 'When to use ClarCV in this flow' },
        {
          type: 'p',
          text: 'When the bottleneck is the file: upload, read the analysis, apply an ATS template, download. Three analyses and two downloads per 30 days on the free plan. If the bottleneck is “I do not know what I was in that job”, inventory on paper first; AI does not remember your life. UI in ES/EN; output CV can follow the ad language.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does AI CV improvement replace a coach?',
              a: 'No. It replaces some formatting and a first clarity pass. A coach or a sector mentor still helps with positioning.',
            },
            {
              q: 'Can I use ChatGPT only?',
              a: 'For drafting, yes. For the PDF you send to an ATS, chat falls short. Combine them or use a CV tool.',
            },
            {
              q: 'Is ClarCV’s AI ChatGPT?',
              a: 'The product uses models via API for analysis and rewriting, not the consumer chat. We do not impersonate ChatGPT as the brand.',
            },
            {
              q: 'Does AI optimisation make the CV generic?',
              a: 'If you give no facts, yes. If you give an inventory and an ad, no. Kill clichés before you download.',
            },
            {
              q: 'Do I have to pay to try?',
              a: 'No. Free account, no card, monthly analysis and download quota. Pro (€7/month) if you iterate a lot.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-demasiado-largo',
    date: '2026-08-19',
    category: 'format',
    related: ['longitud-del-cv', 'experiencia-laboral-con-logros', 'mejorar-el-cv-que-ya-tienes'],
    es: {
      title: 'CV demasiado largo: cómo acortarlo sin perder logros',
      description:
        '¿Tu currículum ocupa 3 páginas? Cómo recortar el PDF que ya tienes: qué borrar primero, viñetas más densas y cuándo pasar de 2 a 1 página.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Si tu CV es demasiado largo, no necesitas otra teoría sobre “una o dos páginas”. Necesitas un método para recortar el archivo que ya tienes esta tarde. Tres páginas de tareas, cursos de 2012 y un Europass denso no se arreglan eligiendo una plantilla más estrecha. Se arreglan borrando ruido y dejando prueba: verbo, alcance, resultado.',
        },
        {
          type: 'p',
          text: 'Esta guía es operativa. La de longitud del CV en este blog explica cuándo una o dos páginas tienen sentido por país y seniority. Aquí el trabajo es: abrir tu PDF, marcar lo que sobra, reescribir viñetas gordas y exportar de nuevo. Si el diseño (Canva, columnas) infla el documento, cambia el continente después de cortar el texto. ClarCV (clarcv.com) sirve para regenerar una plantilla ATS cuando el contenido ya cabe.',
        },
        {
          type: 'p',
          text: 'No reduzcas la fuente a 8 pt para “entrar en una página”. El ATS quizá lea el texto; el humano no. Tampoco borres el único logro cuantificado de tu último empleo para ahorrar cinco líneas.',
        },
        { type: 'h2', text: 'Diagnóstico de cinco minutos' },
        {
          type: 'ul',
          items: [
            '¿La primera pantalla (tercio superior) dice quién eres y para qué puesto, o solo una foto y un objetivo genérico?',
            '¿Los últimos 5–8 años ocupan menos espacio que prácticas de 2014?',
            '¿Hay viñetas que el título ya implica (“atención al cliente” en un puesto de cajero)?',
            '¿Cursos, hobbies y “referencias bajo petición” empujan a la página tres?',
            '¿Puedes seleccionar el texto? Si no, primero arregla el archivo (Canva/imagen), luego recorta.',
          ],
        },
        { type: 'h2', text: 'Orden de corte (no empieces por los logros)' },
        {
          type: 'p',
          text: 'Primero duplicados: si el resumen y la primera viñeta dicen lo mismo, fusiona. Segundo, datos que el mercado no pide (dirección completa, estado civil, DNI). Tercero, experiencia antigua no relacionada: empresa, cargo, fechas y una línea. Cuarto, cursos introductorios y software obvio (Word, Windows). Quinto, hobbies genéricos. Los números de tu último rol se quedan.',
        },
        {
          type: 'p',
          text: 'Antes: cuatro viñetas “responsable de informes, reuniones, Excel y coordinación”. Después: “Monté el reporting semanal en Excel para 14 tiendas; el cierre pasó de el viernes a el miércoles.” Misma persona, un tercio del espacio, más señal.',
        },
        { type: 'h2', text: 'Densificar, no comprimir tipografía' },
        {
          type: 'p',
          text: 'Une acción + alcance + resultado en una o dos líneas. Tres a cinco viñetas en el puesto actual; una o dos en los antiguos. Agrupa promociones en la misma empresa. Si tras eso sigues en tres páginas y eres junior, sobra historia. Si eres senior y dos páginas están llenas de impacto, dos páginas están bien: no recortes escala para cumplir un mito de una página.',
        },
        { type: 'h2', text: 'Cuando el “largo” es el diseño' },
        {
          type: 'p',
          text: 'Márgenes enormes, foto a media página, skill bars y bloques de color alargan el PDF sin añadir hechos. Pasa el contenido a una columna, márgenes ~1.5 cm, cuerpo 10–11.5 pt. Europass denso: migra a un CV moderno (guía en este blog) y entonces recorta competencias genéricas.',
        },
        { type: 'h2', text: 'Cómo usar ClarCV aquí' },
        {
          type: 'p',
          text: 'Sube el Word o PDF largo. El análisis señala claridad y estructura; las plantillas ATS evitan que el recorte se convierta en un Canva de dos columnas. Tú decides qué evidencia queda. Empieza gratis (3 análisis y 2 descargas / 30 días, sin tarjeta). Pro 7 €/mes si iteras versiones por oferta.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿Tres páginas están prohibidas?',
              a: 'En empresa privada, casi siempre sobran. Académico, médico o portfolio son excepciones; para un portal corporativo envía 1–2 páginas y enlaza el resto.',
            },
            {
              q: '¿Borro empleos de hace quince años?',
              a: 'No hace falta borrar el rastro. Redúcelos a una línea. La discontinuidad total a veces genera más preguntas que un resumen honesto.',
            },
            {
              q: '¿Una página con letra minúscula cuenta?',
              a: 'No. Si no se lee en el móvil del reclutador, has fallado el recorte. Mejor dos páginas legibles.',
            },
            {
              q: '¿Esto sustituye la guía de cuántas páginas?',
              a: 'No. Aquella decide el objetivo (1 vs 2 según perfil). Esta recorta el archivo que ya tienes para llegar a ese objetivo.',
            },
            {
              q: '¿Puedo pedir a la IA que “lo deje en una página”?',
              a: 'Puede acortar frases. Si no le das qué conservar, borra prueba. Revisa cada corte. ClarCV no inventa que quepa: tú firmas el contenido.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CV too long: how to shorten it without losing achievements',
      description:
        'Is your résumé three pages? How to cut the PDF you already have: what to delete first, denser bullets, and when to go from two pages to one.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'If your CV is too long, you do not need another theory about “one or two pages”. You need a method to cut the file you already have this afternoon. Three pages of duties, 2012 courses, and a dense Europass are not fixed by picking a narrower template. They are fixed by deleting noise and keeping proof: verb, scope, result.',
        },
        {
          type: 'p',
          text: 'This guide is operational. The CV length article on this blog explains when one or two pages make sense by country and seniority. The job here is: open your PDF, mark what goes, rewrite fat bullets, export again. If design (Canva, columns) inflates the document, change the shell after you cut the text. ClarCV (clarcv.com) regenerates an ATS template once the content already fits.',
        },
        {
          type: 'p',
          text: 'Do not shrink the font to 8 pt to “fit one page”. ATS might still read it; a human will not. Do not delete the only quantified achievement from your last job to save five lines.',
        },
        { type: 'h2', text: 'Five-minute diagnosis' },
        {
          type: 'ul',
          items: [
            'Does the first screen (top third) say who you are and for which role, or only a photo and a generic objective?',
            'Do the last 5–8 years take less space than internships from 2014?',
            'Are there bullets the job title already implies (“customer service” in a cashier role)?',
            'Do courses, hobbies, and “references on request” push you to page three?',
            'Can you select the text? If not, fix the file (Canva/image) first, then cut.',
          ],
        },
        { type: 'h2', text: 'Cut order (do not start with achievements)' },
        {
          type: 'p',
          text: 'First, duplicates: if the summary and the first bullet say the same thing, merge. Second, data the market does not ask for (full address, marital status, ID numbers). Third, unrelated old experience: employer, title, dates, one line. Fourth, intro courses and obvious software (Word, Windows). Fifth, generic hobbies. Numbers from your latest role stay.',
        },
        {
          type: 'p',
          text: 'Before: four bullets “responsible for reports, meetings, Excel, and coordination”. After: “Built weekly Excel reporting for 14 stores; close moved from Friday to Wednesday.” Same person, a third of the space, more signal.',
        },
        { type: 'h2', text: 'Densify, do not compress type' },
        {
          type: 'p',
          text: 'Join action + scope + result in one or two lines. Three to five bullets in the current role; one or two in older ones. Group promotions at the same company. If you are still on three pages as a junior, history has to go. If you are senior and two pages are full of impact, two pages are fine: do not cut scale to satisfy a one-page myth.',
        },
        { type: 'h2', text: 'When “too long” is the design' },
        {
          type: 'p',
          text: 'Huge margins, a half-page photo, skill bars, and colour blocks lengthen the PDF without adding facts. Move content to one column, ~1.5 cm margins, 10–11.5 pt body. Dense Europass: migrate to a modern CV (guide on this blog) and then cut generic competences.',
        },
        { type: 'h2', text: 'How to use ClarCV here' },
        {
          type: 'p',
          text: 'Upload the long Word or PDF. Analysis flags clarity and structure; ATS templates stop the cut from becoming a two-column Canva file. You decide which evidence remains. Start free (3 analyses and 2 downloads / 30 days, no card). Pro €7/month if you iterate versions per vacancy.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Are three pages forbidden?',
              a: 'In the private sector, they almost always overflow. Academic, medical, or portfolio cases are exceptions; for a corporate portal send 1–2 pages and link the rest.',
            },
            {
              q: 'Should I delete jobs from fifteen years ago?',
              a: 'You do not have to erase the trace. Shrink them to one line. A total gap sometimes raises more questions than an honest one-liner.',
            },
            {
              q: 'Does one page in tiny type count?',
              a: 'No. If it cannot be read on a recruiter’s phone, the cut failed. Prefer two readable pages.',
            },
            {
              q: 'Does this replace the how-many-pages guide?',
              a: 'No. That one sets the target (1 vs 2 by profile). This one cuts the file you already have to reach that target.',
            },
            {
              q: 'Can I ask AI to “make it one page”?',
              a: 'It can shorten sentences. If you do not say what to keep, it deletes proof. Review every cut. ClarCV does not invent a fit: you sign the content.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
  {
    slug: 'cv-para-portales-empleo',
    date: '2026-08-19',
    category: 'ats',
    related: ['revisar-cv-online-ats', 'cv-para-mercadona', 'cv-canva-y-ats'],
    es: {
      title: 'CV para InfoJobs, LinkedIn y portales: qué archivo enviar',
      description:
        'Qué currículum subir a InfoJobs, LinkedIn Easy Apply y otros portales en España: PDF vs Word, ATS, por qué falla Canva, y cómo preparar el archivo que ya tienes en ClarCV. No somos InfoJobs.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Un CV para InfoJobs o para LinkedIn Easy Apply no es un diseño para impresionar a tu familia. Es un archivo que un parser de portal va a trocear: nombre, fechas, puestos, skills. Si buscas “cv para infojobs” o “currículum linkedin easy apply”, el error habitual es subir el mismo PDF de Canva que usas para networking. El portal extrae mal el texto; el ranking interno te deja abajo sin que lo sepas.',
        },
        {
          type: 'p',
          text: 'ClarCV no es InfoJobs ni LinkedIn. Esta guía habla del documento que adjuntas en esos canales (y en Indeed, Tecnoempleo, webs de empresa). Qué formato elegir, qué prueba hacer antes de pulsar Enviar, y cuándo regenerar una plantilla ATS a partir del archivo que ya tienes. Empieza en clarcv.com si quieres análisis y PDF parseable; el anuncio se sigue publicando en el portal de siempre.',
        },
        {
          type: 'p',
          text: 'No intentes copiar la plantilla oficial del portal. Completa sus campos con honestidad y adjunta un PDF de una columna, texto seleccionable, keywords de ESA oferta. Dos sistemas distintos: el formulario y el adjunto. Ambos tienen que contar la misma historia (fechas, empresas).',
        },
        { type: 'h2', text: 'Qué espera un portal (ATS ligero o pesado)' },
        {
          type: 'p',
          text: 'InfoJobs, LinkedIn y la mayoría de career sites usan software para almacenar y filtrar. No es el mismo motor que Workday en una multinacional, pero falla igual con PDF-imagen, columnas rotas e iconos en lugar de email. La prueba del bloc de notas de la guía “revisar CV / ATS” aplica igual aquí.',
        },
        {
          type: 'ul',
          items: [
            'PDF con texto seleccionable, salvo que el anuncio pida Word.',
            'Nombre de archivo claro: Nombre_Apellido_Puesto.pdf.',
            'Perfil del portal y adjunto alineados (mismo cargo actual, mismas fechas).',
            'Foto: sigue la costumbre del mercado y las reglas del portal; no dejes el email solo en el encabezado gráfico.',
            'No subas un Europass de cuatro páginas “por si acaso” si nadie lo pidió.',
          ],
        },
        { type: 'h2', text: 'InfoJobs y similares: el adjunto manda' },
        {
          type: 'p',
          text: 'Rellenar el perfil del portal no sustituye un buen PDF. Muchos reclutadores descargan el adjunto o lo envían al cliente. Un perfil completo + un Canva ilegible = filtro flojo. Adapta el adjunto a la oferta (20 minutos, guía de adaptar CV). No copies el anuncio entero; usa herramientas y títulos que sean verdad.',
        },
        { type: 'h2', text: 'LinkedIn Easy Apply' },
        {
          type: 'p',
          text: 'Easy Apply a veces envía tu perfil de LinkedIn más un archivo. Si el adjunto contradice LinkedIn (fechas, cargo), restas puntos. Mantén el mismo mes/año. El CV puede estar en el idioma de la vacante; el perfil puede ser bilingüe. No uses Easy Apply con un infográfico. Detalle de alineación CV–LinkedIn en la guía de este blog.',
        },
        { type: 'h2', text: 'PDF o Word en portales españoles' },
        {
          type: 'p',
          text: 'Si no especifican, PDF limpio. Si piden Word, .docx. Nunca PNG ni “foto del CV”. Si el portal rechaza el peso, comprime la foto; no rasterices el texto. Guía PDF vs Word en este blog.',
        },
        { type: 'h2', text: 'Preparar el archivo en ClarCV' },
        {
          type: 'p',
          text: 'Sube el CV que ya usas en el portal. El análisis señala claridad y fricción típica de parseo. Aplica una plantilla ATS, descarga, súbelo de nuevo al portal. Gratis: 3 análisis y 2 descargas cada 30 días, sin tarjeta. Pro 7 €/mes. No publicamos ofertas ni sustituimos InfoJobs.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿InfoJobs tiene un formato de CV obligatorio?',
              a: 'Sigue las instrucciones del anuncio y del perfil. El adjunto suele ser PDF o Word. Un archivo parseable gana a una plantilla “bonita” que el parser no lee.',
            },
            {
              q: '¿Debo usar la plantilla de CV del propio portal?',
              a: 'Puedes rellenar campos. El PDF adjunto profesional y ATS-friendly sigue siendo el documento que muchos procesos reenvían.',
            },
            {
              q: '¿Easy Apply sin adjunto basta?',
              a: 'A veces. Si te deja adjuntar, adjunta. El perfil de LinkedIn no siempre parsea igual que un CV de una columna.',
            },
            {
              q: '¿ClarCV envía candidaturas a InfoJobs?',
              a: 'No. Mejoramos el archivo. Tú lo subes al portal o al email del proceso.',
            },
            {
              q: '¿Puedo usar el mismo PDF en todos los portales?',
              a: 'La estructura sí. Las keywords no: adapta a cada oferta. El diseño ATS puede ser el mismo.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'CVs for InfoJobs, LinkedIn, and job boards: which file to send',
      description:
        'Which résumé to upload to InfoJobs, LinkedIn Easy Apply, and other boards in Spain: PDF vs Word, ATS, why Canva fails, and how to prepare the file you already have in ClarCV. We are not InfoJobs.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'A CV for InfoJobs or LinkedIn Easy Apply is not a design to impress your family. It is a file a board parser will split: name, dates, roles, skills. If you search for a job-board CV or Easy Apply résumé, the usual mistake is uploading the same Canva PDF you use for networking. The board extracts text badly; internal ranking drops you without a message.',
        },
        {
          type: 'p',
          text: 'ClarCV is not InfoJobs or LinkedIn. This guide is about the document you attach in those channels (and Indeed, niche boards, company sites). Which format to choose, which test to run before Submit, and when to regenerate an ATS template from the file you already have. Start at clarcv.com for analysis and a parseable PDF; you still apply on the usual board.',
        },
        {
          type: 'p',
          text: 'Do not try to clone the board’s official template. Fill its fields honestly and attach a one-column PDF, selectable text, keywords from THAT vacancy. Two different systems: the form and the attachment. Both must tell the same story (dates, employers).',
        },
        { type: 'h2', text: 'What a job board expects (light or heavy ATS)' },
        {
          type: 'p',
          text: 'InfoJobs, LinkedIn, and most career sites use software to store and filter. It is not always the same engine as Workday at a multinational, but it fails the same way on image PDFs, broken columns, and icons instead of an email. The notepad test in the “review your CV / ATS” guide applies here too.',
        },
        {
          type: 'ul',
          items: [
            'Selectable-text PDF unless the ad asks for Word.',
            'Clear filename: Name_Surname_Role.pdf.',
            'Board profile and attachment aligned (same current title, same dates).',
            'Photo: follow market custom and board rules; do not leave email only in a graphic header.',
            'Do not upload a four-page Europass “just in case” if nobody asked.',
          ],
        },
        { type: 'h2', text: 'InfoJobs and similar: the attachment matters' },
        {
          type: 'p',
          text: 'Filling the board profile does not replace a good PDF. Many recruiters download the attachment or send it to the client. A complete profile + an unreadable Canva file = a weak filter. Tailor the attachment to the ad (20 minutes, tailor-your-CV guide). Do not paste the whole posting; use tools and titles that are true.',
        },
        { type: 'h2', text: 'LinkedIn Easy Apply' },
        {
          type: 'p',
          text: 'Easy Apply sometimes sends your LinkedIn profile plus a file. If the attachment contradicts LinkedIn (dates, title), you lose points. Keep the same month/year. The CV can match the vacancy language; the profile can be bilingual. Do not Easy Apply with an infographic. CV–LinkedIn alignment is covered in another guide on this blog.',
        },
        { type: 'h2', text: 'PDF or Word on Spanish boards' },
        {
          type: 'p',
          text: 'If they do not specify, a clean PDF. If they ask for Word, .docx. Never a PNG or a “photo of the CV”. If the board rejects the file size, compress the photo; do not rasterise the text. See the PDF vs Word guide on this blog.',
        },
        { type: 'h2', text: 'Prepare the file in ClarCV' },
        {
          type: 'p',
          text: 'Upload the CV you already use on the board. Analysis flags clarity and typical parse friction. Apply an ATS template, download, upload it to the board again. Free: 3 analyses and 2 downloads per 30 days, no card. Pro €7/month. We do not post vacancies or replace InfoJobs.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does InfoJobs require a specific CV format?',
              a: 'Follow the ad and profile instructions. The attachment is usually PDF or Word. A parseable file beats a pretty template the parser cannot read.',
            },
            {
              q: 'Should I use the board’s own CV template?',
              a: 'You can fill fields. A professional ATS-friendly PDF is still the document many processes forward.',
            },
            {
              q: 'Is Easy Apply without an attachment enough?',
              a: 'Sometimes. If it lets you attach, attach. A LinkedIn profile does not always parse like a one-column CV.',
            },
            {
              q: 'Does ClarCV send applications to InfoJobs?',
              a: 'No. We improve the file. You upload it to the board or the process email.',
            },
            {
              q: 'Can I use the same PDF on every board?',
              a: 'The structure yes. The keywords no: tailor to each ad. The ATS layout can stay the same.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];

